import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  Flex,
  FormControl,
  HStack,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  ListItem,
  OrderedList,
  Spinner,
  Text,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  FaArrowRight,
  FaCheck,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaStar,
} from 'react-icons/fa';

import { submitLandingForm } from '../services/landingService';
import { searchProspectGmb } from '../services/landingMarketService';
import { getRateLimitService } from '../services/rateLimitService';
import { logger } from '../utils/logger';
import ProspectMarketAnalysisModal, { useProspectMarketAnalysis } from './ProspectMarketAnalysisModal';

const PRIMARY = '#0066FF';
const INK = '#0A1F44';
const BAR_BG = 'rgba(186, 217, 255, 0.45)';
const BAR_BORDER = 'rgba(120, 170, 230, 0.55)';
const MUTED_INK = 'rgba(10, 31, 68, 0.7)';
const SEARCH_DEBOUNCE_MS = 450;

const rateLimitService = getRateLimitService();
export const SMB_SIGNUP_FOCUS_EVENT = 'smb-signup-focus';

function BusinessOption({ business, onSelect }) {
  return (
    <Box
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      px={3}
      py={2}
      bg="white"
      borderRadius="md"
      _hover={{ bg: 'blue.50' }}
      onClick={() => onSelect(business)}
    >
      <HStack justify="space-between" align="center" spacing={2}>
        <Box flex={1} minW={0}>
          <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
            {business.name}
          </Text>
          <Text fontSize="xs" color="gray.500" noOfLines={1}>
            {[business.primary_type_display, business.address].filter(Boolean).join(' · ')}
          </Text>
        </Box>
        {business.rating != null && (
          <HStack spacing={0.5} flexShrink={0}>
            <Icon as={FaStar} boxSize={3} color="orange.400" />
            <Text fontSize="xs" fontWeight="600" color="gray.700">
              {business.rating.toFixed(1)}
            </Text>
          </HStack>
        )}
      </HStack>
    </Box>
  );
}

const SmbFloatingProspectBar = () => {
  const [stage, setStage] = useState('gmb');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [anchorBusiness, setAnchorBusiness] = useState(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [portalTarget, setPortalTarget] = useState(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [keyboardBottomPx, setKeyboardBottomPx] = useState(null);

  const {
    isOpen: isAnalysisOpen,
    analysisStep,
    analysisProgress,
    marketResolution,
    marketKeyword,
    competitors,
    startAnalysis,
    closeAnalysis,
  } = useProspectMarketAnalysis();

  const isAnalyzing = isAnalysisOpen && analysisProgress < 100;

  const searchWrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const scrollLockRef = useRef(null);
  const gmbInputRef = useRef(null);
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setPortalTarget(document.body);
    }
  }, []);

  const focusPrimaryInput = useCallback(() => {
    setCollapsed(false);
    requestAnimationFrame(() => {
      const input =
        gmbInputRef.current ||
        document.querySelector('[data-testid="smb-floating-gmb-input"]');
      input?.focus({ preventScroll: true });
    });
  }, []);

  useEffect(() => {
    const handleFocusRequest = () => {
      focusPrimaryInput();
    };
    window.addEventListener(SMB_SIGNUP_FOCUS_EVENT, handleFocusRequest);
    return () => window.removeEventListener(SMB_SIGNUP_FOCUS_EVENT, handleFocusRequest);
  }, [focusPrimaryInput]);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;

    const updateKeyboardInset = () => {
      const gap = 8;
      const keyboardInset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardBottomPx(keyboardInset > 0 ? keyboardInset + gap : null);
    };

    updateKeyboardInset();
    vv.addEventListener('resize', updateKeyboardInset);
    vv.addEventListener('scroll', updateKeyboardInset);
    return () => {
      vv.removeEventListener('resize', updateKeyboardInset);
      vv.removeEventListener('scroll', updateKeyboardInset);
    };
  }, []);

  const handleInputFocus = () => {
    scrollLockRef.current = window.scrollY;
    setIsInputFocused(true);
    if (isMobile) {
      requestAnimationFrame(() => {
        if (scrollLockRef.current != null) {
          window.scrollTo(0, scrollLockRef.current);
        }
      });
    }
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
    scrollLockRef.current = null;
  };

  const runSearch = useCallback(async (query) => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      setSearchError('');
      return;
    }

    setIsSearching(true);
    setSearchError('');
    try {
      const result = await searchProspectGmb(trimmed);
      const results = result.businesses || [];
      setSearchResults(results);
      if (!results.length) {
        setSearchError('No businesses found. Try a different search.');
      }
    } catch (err) {
      logger.error('SMB floating bar GMB search failed', err);
      setSearchResults([]);
      setSearchError('Search unavailable. Please try again.');
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (anchorBusiness) return undefined;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      runSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, anchorBusiness, runSearch]);

  const handleSelectBusiness = (business) => {
    setAnchorBusiness(business);
    setSearchQuery(business.name);
    setSearchResults([]);
    setSearchError('');
    setError('');
  };

  const handleGmbSubmit = async (e) => {
    e.preventDefault();
    if (!anchorBusiness) {
      setError('Select your Google Business Profile from the results');
      return;
    }

    setError('');
    setEmailError('');

    try {
      await startAnalysis(anchorBusiness);
    } catch (err) {
      logger.error('SMB floating bar market analysis failed', err);
      setError(err?.message || 'Could not analyze this market. Please try again.');
    }
  };

  const handleConfirmReport = async () => {
    if (!marketResolution?.recommended?.query || !anchorBusiness) return;

    if (!email?.trim() || !email.includes('@')) {
      setEmailError('Please enter a valid business email');
      return;
    }

    if (rateLimitService.isRateLimited('smbHeroSubmit')) {
      setEmailError('Please wait a moment before trying again.');
      return;
    }

    setIsLoading(true);
    setEmailError('');
    setError('');

    const businessName = anchorBusiness.name || 'Business';
    const nichePayload = (marketKeyword || '').slice(0, 100);
    const prospectUrl = anchorBusiness.maps_url || anchorBusiness.website || '';

    try {
      await submitLandingForm(
        email.trim(),
        nichePayload || businessName,
        businessName,
        undefined,
        { prospectUrl },
      );
      closeAnalysis();
      setStage('success');
    } catch (err) {
      logger.error('SMB floating bar submission failed', err);
      setEmailError(err?.message || 'Unable to submit right now. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setStage('gmb');
    setSearchQuery('');
    setSearchResults([]);
    setAnchorBusiness(null);
    setEmail('');
    setEmailError('');
    setError('');
    setSearchError('');
  };

  const bar = (
    <Box
      id="smb-signup-form"
      position="fixed"
      bottom={
        keyboardBottomPx != null
          ? `${keyboardBottomPx}px`
          : { base: 2, md: 4 }
      }
      left={{ base: 2, md: 4 }}
      right={{ base: 2, md: 4 }}
      zIndex={1100}
      pointerEvents="auto"
      data-testid="smb-floating-prospect-bar"
      sx={{ position: 'fixed' }}
      transition="bottom 0.15s ease-out"
    >
      <Box
        maxW="1080px"
        mx="auto"
        bg={BAR_BG}
        borderRadius={{ base: 'lg', md: 'xl' }}
        boxShadow="0 16px 40px -16px rgba(10,31,68,0.25), 0 4px 12px -6px rgba(10,31,68,0.18)"
        border="1px solid"
        borderColor={BAR_BORDER}
        backdropFilter="blur(14px) saturate(140%)"
        sx={{ WebkitBackdropFilter: 'blur(14px) saturate(140%)' }}
        px={{ base: 2.5, md: 4 }}
        py={{ base: collapsed ? 1.5 : 2.5, md: collapsed ? 1.5 : 3 }}
        position="relative"
        overflow="visible"
        maxH={{ base: 'min(42vh, 320px)', md: 'none' }}
      >
        <IconButton
          aria-label={collapsed ? 'Expand signup bar' : 'Minimize signup bar'}
          icon={<Icon as={collapsed ? FaChevronUp : FaChevronDown} boxSize={2.5} />}
          size="xs"
          variant="ghost"
          color={MUTED_INK}
          position="absolute"
          top={1}
          right={1}
          zIndex={2}
          minW="20px"
          h="20px"
          _hover={{ bg: 'rgba(10,31,68,0.08)', color: INK }}
          onClick={() => setCollapsed((c) => !c)}
          data-testid="smb-floating-prospect-bar-toggle"
        />

        {collapsed ? (
          <HStack spacing={3} align="center" justify="space-between" position="relative" zIndex={1} pr={6}>
            <Text color={INK} fontWeight="700" fontSize="sm">
              Get your free competitive report
            </Text>
            <Button
              size="xs"
              bg={PRIMARY}
              color="white"
              borderRadius="md"
              fontWeight="700"
              rightIcon={<Icon as={FaArrowRight} boxSize={2.5} />}
              _hover={{ bg: '#0052CC' }}
              _active={{ bg: '#0052CC' }}
              onClick={() => setCollapsed(false)}
            >
              Open
            </Button>
          </HStack>
        ) : (
          <Flex
            direction={{ base: 'column', md: 'row' }}
            align={{ base: 'stretch', md: 'center' }}
            gap={{ base: 2, md: 4 }}
            position="relative"
            zIndex={1}
            pr={{ base: 0, md: 6 }}
          >
            <VStack
              align={{ base: 'center', md: 'flex-start' }}
              spacing={0.5}
              textAlign={{ base: 'center', md: 'left' }}
              flexShrink={0}
              maxW={{ base: 'full', md: '240px' }}
              display={
                isMobile && isInputFocused && stage !== 'success' ? 'none' : 'flex'
              }
            >
              <Heading
                as="h3"
                fontSize={{ base: 'sm', md: 'md' }}
                color={INK}
                fontWeight="800"
                letterSpacing="-0.01em"
                lineHeight="1.15"
              >
                {stage === 'success'
                  ? 'Check your inbox.'
                  : 'Get your free competitive report.'}
              </Heading>
              <Text fontSize="11px" color={MUTED_INK} lineHeight="1.4" display={{ base: 'none', md: 'block' }}>
                {stage === 'success'
                  ? "We'll email your report within 24 hours. Full dashboard access is $175/mo."
                  : 'Enter your GMB — we map your top competitors and email the report.'}
              </Text>
            </VStack>

            <Box flex="1" w="full">
              {stage === 'gmb' && (
                <form onSubmit={handleGmbSubmit}>
                  <VStack align="stretch" spacing={1.5}>
                    <Flex direction={{ base: 'column', md: 'row' }} gap={2} w="full">
                      <FormControl isRequired flex="1">
                        <Box ref={searchWrapperRef} position="relative">
                          <InputGroup size="sm">
                            <InputLeftElement pointerEvents="none" h="full">
                              <Flex w="16px" h="16px" justify="center" align="center" flexShrink={0}>
                                <Icon as={FaSearch} color={MUTED_INK} boxSize={3} />
                              </Flex>
                            </InputLeftElement>
                            <Input
                              ref={gmbInputRef}
                              placeholder="Search your Google Business Profile"
                              value={searchQuery}
                              onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setAnchorBusiness(null);
                                if (error) setError('');
                              }}
                              onFocus={handleInputFocus}
                              onBlur={handleInputBlur}
                              bg="whiteAlpha.700"
                              color={INK}
                              border="1px solid"
                              borderColor="rgba(10,31,68,0.18)"
                              borderRadius="md"
                              pl="36px"
                              pr={anchorBusiness || isSearching ? '36px' : 3}
                              fontSize="16px"
                              _placeholder={{ color: 'rgba(10,31,68,0.45)' }}
                              _hover={{ borderColor: 'rgba(10,31,68,0.32)' }}
                              _focus={{
                                borderColor: PRIMARY,
                                boxShadow: `0 0 0 1px ${PRIMARY}`,
                                bg: 'whiteAlpha.900',
                              }}
                              data-testid="smb-floating-gmb-input"
                            />
                            {(anchorBusiness || isSearching) && (
                              <Box position="absolute" right={3} top="50%" transform="translateY(-50%)" zIndex={2}>
                                {anchorBusiness ? (
                                  <Icon as={FaCheck} color="green.500" boxSize={3} />
                                ) : (
                                  <Spinner size="sm" color={PRIMARY} />
                                )}
                              </Box>
                            )}
                          </InputGroup>

                          {(searchResults.length > 0 || searchError) && !anchorBusiness && (
                            <Box
                              position="absolute"
                              bottom="calc(100% + 4px)"
                              left={0}
                              right={0}
                              bg="white"
                              borderWidth="1px"
                              borderColor="gray.200"
                              borderRadius="md"
                              shadow="lg"
                              zIndex={50}
                              maxH="160px"
                              overflowY="auto"
                            >
                              {searchError ? (
                                <Text px={3} py={2} fontSize="xs" color="red.500">
                                  {searchError}
                                </Text>
                              ) : (
                                <VStack spacing={0} align="stretch" py={1}>
                                  {searchResults.map((business) => (
                                    <BusinessOption
                                      key={business.place_id || business.name}
                                      business={business}
                                      onSelect={handleSelectBusiness}
                                    />
                                  ))}
                                </VStack>
                              )}
                            </Box>
                          )}
                        </Box>
                      </FormControl>
                      <Button
                        type="submit"
                        size="sm"
                        bg={PRIMARY}
                        color="white"
                        borderRadius="md"
                        fontWeight="700"
                        px={4}
                        rightIcon={isAnalyzing ? <Spinner size="sm" /> : <Icon as={FaArrowRight} boxSize={3} />}
                        isLoading={isAnalyzing}
                        isDisabled={isAnalysisOpen}
                        _hover={{ bg: '#0052CC', transform: 'translateY(-1px)' }}
                        _active={{ bg: '#0052CC' }}
                        boxShadow="0 6px 16px -6px rgba(0,102,255,0.5)"
                        data-testid="smb-floating-gmb-submit"
                      >
                        Continue
                      </Button>
                    </Flex>
                    {error && (
                      <Text color="red.600" fontSize="xs" fontWeight="500">
                        {error}
                      </Text>
                    )}
                    <HStack
                      spacing={4}
                      flexWrap="wrap"
                      justify={{ base: 'center', md: 'flex-start' }}
                      align="center"
                      display={{ base: 'none', md: 'flex' }}
                    >
                      {['Free report by email', 'No credit card', 'Full access $175/mo'].map((item) => (
                        <HStack key={item} spacing={1.5} align="center">
                          <Flex w="14px" h="14px" justify="center" align="center" flexShrink={0}>
                            <Icon as={FaCheckCircle} color={PRIMARY} boxSize={3} />
                          </Flex>
                          <Text mb={0} fontSize="11px" color={MUTED_INK} fontWeight="500" lineHeight="1">
                            {item}
                          </Text>
                        </HStack>
                      ))}
                    </HStack>
                  </VStack>
                </form>
              )}

              {stage === 'success' && (
                <VStack align="stretch" spacing={2}>
                  <HStack
                    spacing={3}
                    bg="whiteAlpha.600"
                    border="1px solid"
                    borderColor="rgba(10,31,68,0.15)"
                    borderRadius="md"
                    px={3}
                    py={2}
                    align="center"
                  >
                    <Flex
                      w="28px"
                      h="28px"
                      bg="green.500"
                      borderRadius="full"
                      align="center"
                      justify="center"
                      flexShrink={0}
                    >
                      <Icon as={FaCheckCircle} color="white" boxSize={3} />
                    </Flex>
                    <VStack align="flex-start" spacing={0} flex="1">
                      <Text fontWeight="700" color={INK} fontSize="sm">
                        Report on the way.
                      </Text>
                      <Text fontSize="xs" color={MUTED_INK} noOfLines={2}>
                        We&apos;re building your competitive report for{' '}
                        <Text as="span" color={INK} fontWeight="600">
                          {anchorBusiness?.name}
                        </Text>
                        . Want the live dashboard? Full access is $175/mo.
                      </Text>
                    </VStack>
                    <Button
                      onClick={resetForm}
                      size="xs"
                      variant="outline"
                      color={INK}
                      borderColor="rgba(10,31,68,0.3)"
                      borderRadius="md"
                      fontWeight="600"
                      _hover={{ bg: 'whiteAlpha.700', borderColor: 'rgba(10,31,68,0.5)' }}
                    >
                      Submit another
                    </Button>
                  </HStack>
                  <Box display={{ base: 'none', md: 'block' }}>
                    <OrderedList spacing={0.5} pl={4} color={MUTED_INK} fontSize="11px">
                      <ListItem>We scan your market and map your top local competitors</ListItem>
                      <ListItem>Your competitive report arrives by email within 24 hours</ListItem>
                      <ListItem>Upgrade anytime for $175/mo full dashboard access</ListItem>
                    </OrderedList>
                  </Box>
                </VStack>
              )}
            </Box>
          </Flex>
        )}
      </Box>
    </Box>
  );

  if (!portalTarget) {
    return null;
  }

  return createPortal(
    <>
      {bar}
      <ProspectMarketAnalysisModal
        isOpen={isAnalysisOpen}
        onClose={closeAnalysis}
        anchorBusiness={anchorBusiness}
        marketKeyword={marketKeyword}
        competitors={competitors}
        analysisProgress={analysisProgress}
        analysisStep={analysisStep}
        email={email}
        onEmailChange={(value) => {
          setEmail(value);
          if (emailError) setEmailError('');
        }}
        emailError={emailError}
        onConfirm={handleConfirmReport}
        isConfirming={isLoading}
      />
    </>,
    portalTarget,
  );
};

export default SmbFloatingProspectBar;
